<template>
  <div>
    <b-row v-if="!setGlobalLoading && errorsMessage && errorsMessage.length" class="alert-messages pt-3">
      <b-col>
        <b-alert
          show dismissible
          variant="danger"
          v-for="(alertMessage, index) in errorsMessage"
          :key="'alert-msg-' + index.toString()"
          class="alert-height pr-5"
        ><div v-html="alertMessage"></div></b-alert>
      </b-col>
    </b-row>
    <StepTitle>
      <div v-html="title" class="how-can"></div>
      <template v-if="isMFA" #subtitle>
        <div class="customer-form-subtitle">
          <span v-if="showMFAChoices">Select how you would like to receive your verification code:</span>
          <span v-else-if="selectedMFAMethod">Please enter the 6-digit code we just {{MFAAction}}</span>
        </div>
      </template>
    </StepTitle>
    <b-form @submit.prevent="submit()" class="customer-verify-form">
      <div class="customer-form-wrapper">
        <template v-if="isMFA">
          <template v-if="showMFAChoices">
            <b-row class="justify-content-md-center boxes">
              <b-col class="customer-match-form">
                <div class="choice-fields-input">
                  <b-form-group label-for="mfa-method">
                    <b-form-radio-group id="mfa-method" v-model="selectedMFAMethod" name="mfa-method" stacked>
                      <b-form-radio v-for="option in MFAChoices" :key="option.value" :value="option.value"
                                    :class="{'selected': option.value === selectedMFAMethod}" :for="'option-'+option.value"
                                    :id="'option-'+option.value">
                        <div>{{option.label}}</div>
                      </b-form-radio>
                    </b-form-radio-group>
                  </b-form-group>
                </div>
              </b-col>
            </b-row>
          </template>
          <template v-else-if="selectedMFAMethod">
            <b-row class="justify-content-md-center boxes">
              <b-col class="customer-match-form">
                <div class="customer-match-formgroup">
                  <div class="mfa-code-container">
                    <div class="mfa-code-group">
                      <input
                        v-for="(digit, index) in 3"
                        :key="'first-' + index"
                        :ref="'mfaInput' + index"
                        v-model="mfaDigits[index]"
                        type="text"
                        maxlength="1"
                        class="mfa-digit-input"
                        :class="{ 'first-box': index === 0, 'last-box': index === 2, 'highlighted': mfaDigits[index], 'error': mfaError }"
                        @input="handleDigitInput(index, $event)"
                        @keydown="handleKeydown(index, $event)"
                        @paste="handlePaste($event)"
                      />
                    </div>
                    <div class="mfa-dash">-</div>
                    <div class="mfa-code-group">
                      <input
                        v-for="(digit, index) in 3"
                        :key="'second-' + (index + 3)"
                        :ref="'mfaInput' + (index + 3)"
                        v-model="mfaDigits[index + 3]"
                        type="text"
                        maxlength="1"
                        class="mfa-digit-input"
                        :class="{ 'first-box': index === 0, 'last-box': index === 2, 'highlighted': mfaDigits[index + 3], 'error': mfaError }"
                        @input="handleDigitInput(index + 3, $event)"
                        @keydown="handleKeydown(index + 3, $event)"
                        @paste="handlePaste($event)"
                      />
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </template>
        </template>
        <template v-else>
          <VerificationField v-for="(field, index) in session.required_fields"
                             :key="'verification-field-' + index.toString()"
                             :method="field"
                             :value="values"
                             :placeholders="placeholders"
                             all-fields-required
          >
          </VerificationField>
          <input id="country" type="hidden" v-model="values.country" />
        </template>
      </div>
      <div class="customer-form-wrapper customer-form-btn-wrapper">
        <div class="main-buttons-row">
          <button v-if="session && session.can_undo && setGlobalLoading" @click="goBack()" class="btn-base input-choice-btn input-btn new-btn-outline-success stroke-2x" type="button">Back</button>
          <button @click="$event.stopPropagation()" :class="{'submit-and-verify': !setGlobalLoading}" type="submit" class="btn-base input-choice-btn input-btn text-nowrap">
            <template v-if="isMFA && showMFAChoices"> Send Code </template>
            <template v-else-if="setGlobalLoading"> Submit </template>
            <template v-else>
              SUBMIT & CONTINUE
              <span class="float-none float-sm-right pr-2">
                <font-awesome-icon :icon="['fas', 'arrow-right']"></font-awesome-icon>
              </span>
            </template>
          </button>
        </div>
        <button v-if="isMFA && !showMFAChoices && selectedMFAMethod" @click="selectMFAMethod()" class="btn-base input-choice-btn input-btn text-nowrap mfa-resend-btn" type="button">Send a new code</button>
      </div>
    </b-form>
    <div ref="translation_detector" style="display:none;">{{translateDetectString}}</div>
    <b-link block class="centered-block close-button" variant="secondary" v-if="session && session.is_popup && !session.can_undo" @click="goBack()">&times;</b-link>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import {
  CustomerVerifyMessages,
  TranslateTestString,
  SessionResult,
  SessionStatus,
  MFAStatus,
  MFAMethod
} from '@/constants'
import {setPlaceholders, stopMouseflowRecording, getErrorData} from '@/utils'
import StepTitle from './StepTitle'
import VerificationField from './VerificationField'

export default {
  name: 'CustomerVerify',
  components: {
    VerificationField,
    StepTitle
  },
  props: {
    getCart: {
      type: Boolean,
      default: true
    },
    setGlobalLoading: {
      type: Boolean,
      default: true
    }
  },
  data: function() {
    return {
      translateDetectString: TranslateTestString,
      values: {
        first_name: null,
        last_name: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        phone: null,
        email: null,
        country: null
      },
      placeholders: {
        first_name: '',
        last_name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        email: '',
        country: ''
      },
      errorsMessage: null,
      showMFAChoices: false,
      selectedMFAMethod: null,
      MFAChoices: [],
      MFACode: null,
      mfaDigits: ['', '', '', '', '', ''],
      mfaError: false
    }
  },
  computed: {
    ...mapState(['session', 'cartIsOpen']),
    isMFA: function() {
      return this.session && (this.session.status === SessionStatus.Verified) &&
        (this.session.mfa_status === MFAStatus.Pending)
    },
    title: function() {
      return this.isMFA ? (this.showMFAChoices ? 'Send verification code' : 'Enter code to continue') : this.session.verify_message
    },
    MFAAction: function() {
      if (this.selectedMFAMethod === MFAMethod.SMS) {
        return 'texted to you'
      } else if (this.selectedMFAMethod === MFAMethod.Email) {
        return 'emailed to you'
      } else if (this.selectedMFAMethod === MFAMethod.Voice) {
        return 'called you with'
      }
      return 'sent to you'
    }
  },
  methods: {
    obfuscateEmail: function(email) {
      const parts = email.split('@')
      if (parts.length !== 2) {
        return email
      }
      const name = parts[0]
      const domain = parts[1]
      let obfuscatedName = ''
      if (name.length <= 2) {
        obfuscatedName = name[0] + '*'
      } else {
        obfuscatedName = name[0] + '*'.repeat(name.length - 2) + name[name.length - 1]
      }
      const domainParts = domain.split('.')
      if (domainParts.length < 2) {
        return obfuscatedName + '@' + domain
      }
      const domainName = domainParts.slice(0, -1).join('.')
      const tld = domainParts[domainParts.length - 1]
      let obfuscatedDomainName = ''
      if (domainName.length <= 2) {
        obfuscatedDomainName = domainName[0] + '*'
      } else {
        obfuscatedDomainName = domainName[0] + '*'.repeat(domainName.length - 2) + domainName[domainName.length - 1]
      }
      return obfuscatedName + '@' + obfuscatedDomainName + '.' + tld
    },
    obfuscatePhone: function(phone, countryCode) {
      const digits = phone.replace(/\D/g, '')
      const nationalNumber = digits.startsWith(countryCode) ? digits.slice(countryCode.length) : digits

      if (countryCode === '1') {
        // North American Numbering Plan
        return `(${nationalNumber.slice(0, 3)}) *** - **` + nationalNumber.slice(-2)
      }

      // International format
      return `+${countryCode}-` + nationalNumber.slice(0, 2) + '*'.repeat(nationalNumber.length - 4) +
        nationalNumber.slice(-2)
    },
    updateData: function(field, val) {
      this.values[field] = val
    },
    processPostMatch: function(session) {
      if (this.getCart) {
        this.$store.dispatch('get_cart')
      }
      this.init()
      this.$emit('done')
      if (!this.setGlobalLoading) {
        this.$emit('verifying', false)
      }
    },
    updateMFAChoices: function() {
      const choices = []

      if (this.session && this.session.customer) {
        if (this.session.customer.phone) {
          choices.push({value: MFAMethod.SMS,
            label: 'Text me at ' + this.obfuscatePhone(
              this.session.customer.phone, this.session.customer.phone_country_code)})
        }
        if (this.session.customer.email) {
          choices.push({value: MFAMethod.Email,
            label: 'Email me at ' + this.obfuscateEmail(this.session.customer.email)})
        }

        if (this.session.mfa_method === MFAMethod.Email) {
          choices.reverse()
        }
      }

      this.MFAChoices = choices

      if (choices.length) {
        this.selectedMFAMethod = choices[0].value
      }
    },
    handleDigitInput(index, event) {
      // Clear error state when user starts typing
      if (this.mfaError) {
        this.mfaError = false
      }

      const value = event.target.value

      // Check if this might be a paste operation (multiple characters)
      if (value.length > 1) {
        const digits = value.replace(/\D/g, '').slice(0, 6)
        if (digits.length > 0) {
          // Handle pasted content
          for (let i = 0; i < 6; i++) {
            this.mfaDigits[i] = digits[i] || ''
          }
          this.updateMFACode()

          // Focus the next empty input or the last one
          const nextEmptyIndex = this.mfaDigits.findIndex(digit => !digit)
          const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

          this.$nextTick(() => {
            const input = this.$refs['mfaInput' + focusIndex]
            if (input) {
              if (Array.isArray(input)) {
                input[0].focus()
              } else {
                input.focus()
              }
            }
          })
        }
        return
      }

      if (value && /^\d$/.test(value)) {
        this.mfaDigits[index] = value
        this.updateMFACode()
        // Move to next input
        if (index < 5) {
          const nextInput = this.$refs['mfaInput' + (index + 1)]
          if (nextInput && nextInput[0]) {
            nextInput[0].focus()
          }
        }
      } else if (value === '') {
        this.mfaDigits[index] = ''
        this.updateMFACode()
      } else {
        // Invalid input, clear the field
        event.target.value = ''
      }
    },
    handleKeydown(index, event) {
      // Handle paste shortcuts (Ctrl+V or Cmd+V)
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
        // Try to read from clipboard API if available
        if (navigator.clipboard && navigator.clipboard.readText) {
          event.preventDefault()
          navigator.clipboard.readText().then(text => {
            // Clear error state when pasting
            if (this.mfaError) {
              this.mfaError = false
            }

            const digits = text.replace(/\D/g, '').slice(0, 6)
            if (digits.length > 0) {
              for (let i = 0; i < 6; i++) {
                this.$set(this.mfaDigits, i, digits[i] || '')
              }
              this.updateMFACode()

              // Focus appropriate input
              const nextEmptyIndex = this.mfaDigits.findIndex(digit => !digit)
              const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

              this.$nextTick(() => {
                const input = this.$refs['mfaInput' + focusIndex]
                if (input) {
                  if (Array.isArray(input)) {
                    input[0].focus()
                  } else {
                    input.focus()
                  }
                }
              })
            }
          }).catch(() => {
            // If clipboard API fails, let browser handle normally
          })
        }
        return
      }

      // Handle backspace
      if (event.key === 'Backspace' && !this.mfaDigits[index] && index > 0) {
        const prevInput = this.$refs['mfaInput' + (index - 1)]
        if (prevInput && prevInput[0]) {
          prevInput[0].focus()
        }
      }
      // Only allow digits, backspace, delete, tab, and arrow keys
      if (!/^[0-9]$/.test(event.key) &&
          !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault()
      }
    },
    handlePaste(event) {
      event.preventDefault()

      // Clear error state when pasting
      if (this.mfaError) {
        this.mfaError = false
      }

      // Get clipboard data with fallback for different browsers
      let pastedData = ''
      if (event.clipboardData) {
        pastedData = event.clipboardData.getData('text/plain') || event.clipboardData.getData('text')
      }

      const digits = pastedData.replace(/\D/g, '').slice(0, 6)

      if (digits.length > 0) {
        // Clear target input to prevent browser default behavior
        if (event.target) {
          event.target.value = ''
        }

        // Fill digits with proper Vue reactivity
        for (let i = 0; i < 6; i++) {
          this.$set(this.mfaDigits, i, digits[i] || '')
        }
        this.updateMFACode()

        // Focus appropriate input
        const nextEmptyIndex = this.mfaDigits.findIndex(digit => !digit)
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5

        this.$nextTick(() => {
          const input = this.$refs['mfaInput' + focusIndex]
          if (input) {
            if (Array.isArray(input)) {
              input[0].focus()
            } else {
              input.focus()
            }
          }
        })
      }
    },
    updateMFACode() {
      this.MFACode = this.mfaDigits.join('')
    },
    clearMFADigits() {
      this.mfaDigits = ['', '', '', '', '', '']
      this.MFACode = null
      this.mfaError = false
    },
    selectMFAMethod() {
      this.clearMFADigits()
      this.updateMFAChoices()
      this.showMFAChoices = true
    },
    sendMFACode() {
      if (this.selectedMFAMethod) {
        this.$store.commit('busy')
        this.$store.dispatch('mfa_send', {method: this.selectedMFAMethod})
          .then(session => {
            this.clearMFADigits()
            this.showMFAChoices = false
            this.$store.commit('ready')
          })
          .catch(err => {
            if (err.response && err.response.status === 400) {
              let errors = []

              Object.keys(err.response.data).forEach(key => {
                errors = errors.concat(err.response.data[key])
              })
              if (!this.setGlobalLoading) {
                this.errorsMessage = errors
              } else {
                this.$store.commit('error', errors)
              }
            } else {
              this.$store.commit('critical_error', err)
            }
            this.$store.commit('ready')
          })
      }
    },
    verify: function() {
      this.$root.$emit('toggle-processing-gifs', CustomerVerifyMessages)
      if (this.setGlobalLoading) {
        this.$store.commit('busy')
      } else {
        this.$emit('verifying', true)
      }
      const path = this.isMFA ? 'mfa_verify' : 'verify'
      const params = this.isMFA ? {code: this.MFACode} : this.values
      this.$store.dispatch(path, params)
        .then(session => this.processPostMatch(session))
        .catch(err => {
          if (err.response && err.response.status === 404) {
            if (this.isMFA) {
              this.mfaError = true
            }
            let message = this.isMFA ? 'Sorry, but the code you entered is invalid.'
              : "Sorry, but we couldn't verify your information." +
              "<br/>Make sure you typed it correctly and it's the same as when you placed your order."
            if (!this.setGlobalLoading) {
              this.errorsMessage = [message]
            } else {
              this.$store.commit('error', message)
            }
          } else if (err.response && err.response.status === 403) {
            const error = getErrorData(err)
            console.log(error)
            this.$store.commit('no_match', error.message, SessionResult.LifelineNoVerify)
          } else if (err.response && err.response.status === 400) {
            let errors = []

            Object.keys(err.response.data).forEach(key => {
              errors = errors.concat(err.response.data[key])
            })
            if (!this.setGlobalLoading) {
              this.errorsMessage = errors
            } else {
              this.$store.commit('error', errors)
            }
          } else if (err.response && err.response.status === 303 && err.response.data) {
            this.$store.commit('session', err.response.data)
            this.processPostMatch(err.response.data)
          } else {
            this.$store.commit('critical_error', err)
          }
          if (!this.setGlobalLoading) {
            this.$emit('verifying', false)
          } else {
            this.$store.commit('ready')
          }
        })
        .finally(() => this.$root.$emit('toggle-processing-gifs', null))
    },
    submit: function() {
      if (this.isMFA && this.showMFAChoices) {
        this.sendMFACode()
      } else {
        this.verify()
      }
    },
    goBack: function() {
      this.$emit('back')
    },
    init: function() {
      if (this.isMFA) {
        if (this.session.mfa_method) {
          this.showMFAChoices = false
          this.selectedMFAMethod = this.session.mfa_method
        } else {
          this.selectMFAMethod()
        }
      }
    }
  },
  mounted() {
    stopMouseflowRecording()
    this.$store.dispatch('wait_for_translation', {element: this.$refs['translation_detector']})
      .then(value => {
        setTimeout(() => {
          setPlaceholders(this.placeholders)
        }, 100)
      })
  },
  beforeDestroy() {
    this.$root.$emit('toggle-processing-gifs', null)
  },
  created() {
    this.init()
  }
}
</script>

<style scoped>
.mfa-code-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
}

.mfa-code-group {
  display: flex;
}

.mfa-digit-input {
  width: 95px;
  height: 129px;
  text-align: center;
  font-size: 78px;
  font-weight: 300;
  background: #FFF;
  border: 1px solid #B1B1B1;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
  outline: none;
  transition: border-color 0.2s ease;
  font-family: 'Open Sans', sans-serif;
}

.mfa-digit-input:focus {
  border: 1.5px solid #F8B600;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
}

.mfa-digit-input.highlighted {
  border: 1.5px solid #F8B600;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.15);
}

.mfa-digit-input.error {
  border: 1px solid #FF7663;
}

.mfa-digit-input.first-box {
  border-radius: 5px 0 0 5px;
}

.mfa-digit-input.last-box {
  border-radius: 0 5px 5px 0;
}

.mfa-code-group .mfa-digit-input:not(.first-box):not(.last-box) {
  border-radius: 0;
  border-left: none;
}

.mfa-code-group .mfa-digit-input:not(.first-box) {
  border-left: none;
}

.mfa-dash {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0 8px;
  display: flex;
  align-items: center;
}

.mfa-resend-btn {
  background: transparent;
  border: none;
  color: var(--theme_color);
  text-decoration: none;
  font-weight: normal;
  text-transform: none;
  min-height: auto;
  padding: 8px 16px;
  box-shadow: none;
  margin-top: 12px;
  font-size: 18px;
  max-width: 200px;
  display: inline-block;
}

.mfa-resend-btn:hover {
  background: transparent;
  color: var(--button_hover_color);
  text-decoration: none;
}

.mfa-resend-btn:focus {
  background: transparent;
  color: var(--theme_color);
  text-decoration: none;
  box-shadow: none;
}

.customer-form-btn-wrapper {
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  gap: 0 !important;
}

.main-buttons-row {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-bottom: 10px;
}

.main-buttons-row button {
  max-width: 50%;
  margin-bottom: 0;
}

/* Override customer-form-wrapper styling */
.customer-form-wrapper {
  background: transparent !important;
}

/* Desktop override for customer-form-wrapper */
@media only screen and (min-width: 1200px) {
  .customer-form-wrapper {
    width: 60% !important;
    max-width: 60% !important;
  }
}

/* Responsive styling for MFA inputs on mobile */
@media only screen and (max-width: 768px) {
  .mfa-digit-input {
    width: 60px;
    height: 80px;
    font-size: 48px;
  }

  .mfa-code-container {
    gap: 8px;
  }

  .mfa-dash {
    font-size: 18px;
    margin: 0 4px;
  }
}

@media only screen and (max-width: 480px) {
  .mfa-digit-input {
    width: 45px;
    height: 65px;
    font-size: 36px;
  }

  .mfa-code-container {
    gap: 6px;
  }

  .mfa-dash {
    font-size: 16px;
    margin: 0 3px;
  }

  .main-buttons-row button {
    max-width: 100% !important;
  }
}
</style>
